import { MatchPhone, VenueMetric } from "refsix-js-models";
import { metersPerSecondsToKphOrMph, metersToKmOrMiles } from "refsix-core";
import { TFunction } from "react-i18next";

import {
    BoardMetricRankingPublic,
    BoardRankingSetPublic,
    BoardRecordPublic,
    BoardRecordSetPublic,
} from "../../services/venueService";
import { sumSprints, VenuePersonalHistory } from "./venueHistory";
import { ANONYMOUS_DISPLAY_NAME } from "../../services/leaderboardOptInService";

/**
 * The vocabulary shared by the two venue-leaderboard surfaces (result-page ribbons and the
 * leaderboards page).
 *
 * Every value here is in the units the backend and the match document already store —
 * speed m/s, distance metres, sprints a count — and conversion to km/h or mph happens only in
 * `formatMetricValue` at the render edge. Mixing the two is the obvious way to get a resolver
 * that silently compares 32.4 against 9.0, so the rule is: resolvers never see a display unit.
 */
export type { VenueMetric };

export const VENUE_METRICS: VenueMetric[] = [
    "speedMax",
    "distanceTotal",
    "sprintsTotal",
];

/**
 * The short metric name used both for `?section=` deep links and for the record-card testids
 * the Playwright page objects address (`venue-records-month-speed`). One map, so a rename
 * cannot leave the ribbon linking somewhere the page no longer has an anchor for.
 */
export const METRIC_SECTIONS: Record<VenueMetric, string> = {
    speedMax: "speed",
    distanceTotal: "distance",
    sprintsTotal: "sprints",
};

export const LEGEND_SECTION = "legend";

/** Emoji per metric, matching the design reference. Decorative — never the only signal. */
export const METRIC_ICONS: Record<VenueMetric, string> = {
    speedMax: "⚡",
    distanceTotal: "🏃",
    sprintsTotal: "💨",
};

const METRIC_LABEL_KEYS: Record<VenueMetric, string> = {
    speedMax: "leaderboards.VenuePage.topSpeed",
    distanceTotal: "leaderboards.VenuePage.distance",
    sprintsTotal: "leaderboards.VenuePage.sprints",
};

export function metricLabel(metric: VenueMetric, t: TFunction): string {
    return t(METRIC_LABEL_KEYS[metric]);
}

/** This match's value for a metric, in stored units. `null` when the match has no such stat. */
export function matchMetricValue(
    match: MatchPhone | undefined,
    metric: VenueMetric
): number | null {
    const stats = match?.stats;
    if (!stats) {
        return null;
    }
    const raw =
        metric === "sprintsTotal"
            ? sumSprints(stats.sprintsTotal)
            : (stats as any)[metric];
    return typeof raw === "number" && isFinite(raw) && raw > 0 ? raw : null;
}

/** The player's personal best for a metric at this ground, in stored units. */
export function historyBest(
    history: VenuePersonalHistory,
    metric: VenueMetric
): number {
    switch (metric) {
        case "speedMax":
            return history.bestSpeedMax;
        case "distanceTotal":
            return history.bestDistanceTotal;
        case "sprintsTotal":
            return history.bestSprintsTotal;
    }
}

export function recordForMetric(
    set: BoardRecordSetPublic | null | undefined,
    metric: VenueMetric
): BoardRecordPublic | null {
    return set ? set[metric] ?? null : null;
}

export interface FormattedMetricValue {
    /** The number, already converted and rounded for display. */
    value: string;
    /** The unit suffix, or "" for a bare count. */
    unit: string;
}

/**
 * Render a stored value in the user's units.
 *
 * Distance uses two decimals throughout (10.75 km reads as a real distance where 10.8 km reads
 * as a rounding), speed one — matching `metersPerSecondsToKphOrMph`, which the rest of the app
 * already uses — and sprints are a bare integer.
 */
export function formatMetricValue(
    metric: VenueMetric,
    value: number,
    useImperial: boolean,
    t: TFunction
): FormattedMetricValue {
    switch (metric) {
        case "speedMax":
            return {
                value: metersPerSecondsToKphOrMph(value, useImperial),
                unit: t(
                    useImperial
                        ? "leaderboards.VenuePage.speedUnit.imperial"
                        : "leaderboards.VenuePage.speedUnit.metric"
                ),
            };
        case "distanceTotal":
            return {
                value: metersToKmOrMiles(value, useImperial).toFixed(2),
                unit: t(
                    useImperial
                        ? "leaderboards.VenuePage.distanceUnit.imperial"
                        : "leaderboards.VenuePage.distanceUnit.metric"
                ),
            };
        case "sprintsTotal":
            return { value: String(Math.round(value)), unit: "" };
    }
}

/** "10.75 km" / "32.4 km/h" / "72" — the one-string form, for inline copy. */
export function formatMetric(
    metric: VenueMetric,
    value: number,
    useImperial: boolean,
    t: TFunction
): string {
    const { value: text, unit } = formatMetricValue(
        metric,
        value,
        useImperial,
        t
    );
    return unit ? `${text} ${unit}` : text;
}

/**
 * How far a board value may sit from a local match's value and still be the same match, as a
 * fraction of the value. The server stores what the watch submitted and the client re-derives
 * from the match document, so the two agree to a rounding rather than to the bit.
 */
const MATCH_VALUE_TOLERANCE = 0.01;

/**
 * The local match behind a board value, so a row of the player's own can open its result.
 *
 * Board rows carry a display name and a number and nothing else — no match id ever crosses the
 * public boundary — so the match has to be recovered by value. The boards rank personal bests,
 * so the closest of the player's matches at this ground is the one that set it. Returns
 * `undefined` when nothing is close enough, which is the normal answer for a record set on a
 * device this phone never had the match on.
 */
export function matchIdForMetricValue(
    matches: MatchPhone[] | undefined,
    metric: VenueMetric,
    value: number | null | undefined
): string | undefined {
    if (typeof value !== "number" || !isFinite(value) || value <= 0) {
        return undefined;
    }

    let closestId: string | undefined;
    let closestDiff = Infinity;

    (matches || []).forEach((match) => {
        const candidate = matchMetricValue(match, metric);
        if (candidate === null || !match._id) {
            return;
        }
        const diff = Math.abs(candidate - value);
        if (diff < closestDiff) {
            closestDiff = diff;
            closestId = match._id;
        }
    });

    return closestDiff <= value * MATCH_VALUE_TOLERANCE ? closestId : undefined;
}

export interface VenueHistoryGame {
    matchId?: string;
    /** Stored units. */
    value: number;
    homeTeam: string;
    awayTeam: string;
    /** "2 - 2", or null when the match recorded no score. */
    score: string | null;
    /** ISO date string, formatted by the caller so it honours the user's locale. */
    date: string;
    isPB: boolean;
}

/**
 * The player's best games at this ground for one metric, best first.
 *
 * One row per match rather than per value, so two matches that tied still both appear — the
 * list is "your top games here", not "your distinct scores". Matches with no value for the
 * metric are dropped rather than listed as zero.
 */
export function topMatchesByMetric(
    matches: MatchPhone[] | undefined,
    metric: VenueMetric,
    limit: number = 5
): VenueHistoryGame[] {
    const scored: Array<{ match: MatchPhone; value: number }> = [];

    (matches || []).forEach((match) => {
        const value = matchMetricValue(match, metric);
        if (value !== null) {
            scored.push({ match, value });
        }
    });

    scored.sort((a, b) => b.value - a.value);

    return scored.slice(0, limit).map(({ match, value }, index) => ({
        matchId: match._id,
        value,
        homeTeam: match.homeTeam,
        awayTeam: match.awayTeam,
        score: matchScore(match),
        date: match.date,
        // Best-first ordering means only the head row can be the personal best, and only when
        // nothing else ties it — a shared best is not "the" PB.
        isPB: index === 0 && (scored.length < 2 || scored[1].value < value),
    }));
}

function matchScore(match: MatchPhone): string | null {
    const home = match.stats?.goalsHomeTotal;
    const away = match.stats?.goalsAwayTotal;
    if (typeof home !== "number" || typeof away !== "number") {
        return null;
    }
    return `${home} - ${away}`;
}

/**
 * Up to two initials for a board avatar. Display names are opt-in and server-derived
 * ("Jordan C."), so the naive first-letter-of-each-word is right; the fallback keeps a
 * single-word name from rendering an empty circle.
 */
export function initialsOf(displayName: string | null | undefined): string {
    const parts = (displayName || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
        return "?";
    }
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Whether a board row belongs to the signed-in user.
 *
 * Display name is all the public contract exposes — no username ever crosses that boundary — so
 * this is a name comparison by necessity. Gated on `onBoards` because a user who is not on the
 * boards cannot own a row, and two players legitimately sharing a display name must not make a
 * stranger's record read as yours.
 *
 * An anonymous row is never claimed, and that is not an oversight. Every anonymous player on
 * every board carries the identical `ANONYMOUS_DISPLAY_NAME`, so a name comparison would mark
 * all of them — including other people's — as the signed-in user. Losing the "this row is you"
 * highlight is the correct price for choosing anonymity, and the alternative (a per-viewer
 * marker from the server) would mean the board payload knowing who is asking.
 */
export function isMe(
    displayName: string | null | undefined,
    myDisplayName: string | null | undefined,
    onBoards: boolean
): boolean {
    if (displayName === ANONYMOUS_DISPLAY_NAME) {
        return false;
    }
    return !!(
        onBoards &&
        myDisplayName &&
        displayName &&
        displayName === myDisplayName
    );
}

export function rankingForMetric(
    set: BoardRankingSetPublic | null | undefined,
    metric: VenueMetric
): BoardMetricRankingPublic | null {
    return set ? set[metric] ?? null : null;
}

/** The design caps every board at five names (SPEC §5). */
export const BOARD_ROWS = 5;

/**
 * Where the player sits relative to a board.
 *
 *  - `onBoard` — their own row is in `rows`, so the board already answers "where am I?".
 *  - `outsideCut` — they are past the depth the server stores. There is no rank to show, hence
 *    the "100+" label, and the cut is read off the list rather than hardcoded so the client
 *    never has to know the server's cap.
 *  - `notRanked` — the whole board fits and they are not on it: no qualifying result here yet.
 *  - `hidden` — not opted in, so they are on no board and are not pretending to be.
 */
export type YouState = "onBoard" | "outsideCut" | "notRanked" | "hidden";

export interface ResolvedBoard<T> {
    /** The rows to render, in order — top five, or top four plus the player's own row. */
    rows: T[];
    /** Index in `rows` to draw the `•••` break before, or -1. */
    gapBefore: number;
    youState: YouState;
    /** The rank column for an `outsideCut` player, e.g. "100+". Null otherwise. */
    outsideCutLabel: string | null;
}

/**
 * Trim a stored board to what the design shows: the top five, and — when the player is further
 * down — their own row pinned to the bottom after a break, in place of the fifth name.
 *
 * Shared by the Local Legend board and the three metric boards so the "where am I?" rule is
 * written once. That question is the only thing that makes a leaderboard worth opening twice,
 * and it is the half most likely to drift if each board answers it separately.
 */
export function resolveBoardRows<T extends { displayName: string }>(
    all: T[],
    rankedCount: number,
    myDisplayName: string | null | undefined,
    onBoards: boolean
): ResolvedBoard<T> {
    const stored = all || [];
    const myIndex = stored.findIndex((row) =>
        isMe(row.displayName, myDisplayName, onBoards)
    );

    let rows: T[];
    let gapBefore = -1;
    if (myIndex >= BOARD_ROWS) {
        rows = stored.slice(0, BOARD_ROWS - 1).concat([stored[myIndex]]);
        gapBefore = rows.length - 1;
    } else {
        rows = stored.slice(0, BOARD_ROWS);
    }

    let youState: YouState = "hidden";
    if (onBoards) {
        youState =
            myIndex >= 0
                ? "onBoard"
                : rankedCount > stored.length
                ? "outsideCut"
                : "notRanked";
    }

    return {
        rows,
        gapBefore,
        youState,
        outsideCutLabel: youState === "outsideCut" ? `${stored.length}+` : null,
    };
}
